/**
 * The characters a writing sheet practises, from the words it was given.
 *
 * A sheet practises characters, and a List or a reader's saved words are
 * words. Each character is practised once, at the place it first appears, and
 * remembers the word that brought it. That word is what lets the sheet say
 * 行 is háng in 银行 rather than leaving a learner with three readings and no
 * way to choose.
 */

/** One character to practise, and the word it came from if it came from one. */
export interface SheetCharacter {
  readonly character: string;

  /** The first word of more than one character that held it. */
  readonly word: string | undefined;

  /** Where the character sits in that word, counted in characters. */
  readonly at: number;
}

const han = /\p{Script=Han}/u;

/**
 * The words in a typed box.
 *
 * Split on spaces and on the punctuation a list is usually typed with, in
 * either script. A sentence typed without spaces arrives as one long "word",
 * which has no 词典 entry and so names no reading. Its characters are still
 * practised.
 * @param text - Whatever was typed.
 * @returns Each run that holds at least one 汉字.
 */
export function typedWords(text: string): readonly string[] {
  return text
    .split(/[\s,，、;；。.!！?？:：/|"“”'‘’()（）[\]【】《》<>]+/u)
    .filter((word) => han.test(word));
}

/**
 * Every character in the words, once each, in the order they first appear.
 * @param words - The sheet's words, in the order they were given.
 * @param limit - The most characters a sheet takes.
 */
export function sheetCharacters(
  words: readonly string[],
  limit: number,
): readonly SheetCharacter[] {
  const seen = new Set<string>();
  const characters: SheetCharacter[] = [];

  for (const word of words) {
    const letters = Array.from(word);
    const isWord =
      letters.length > 1 && letters.every((letter) => han.test(letter));

    for (const [at, character] of letters.entries()) {
      if (!han.test(character) || seen.has(character)) {
        continue;
      }
      if (characters.length >= limit) {
        return characters;
      }
      seen.add(character);
      characters.push({ character, word: isWord ? word : undefined, at });
    }
  }

  return characters;
}
